import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import communityDiscountAdminQuery from './communityDiscountAdmin.graphql';
import reviewCommunityDiscountMutation from './reviewCommunityDiscount.graphql';
import updateCommunityDiscountSettingsMutation from './updateCommunityDiscountSettings.graphql';

const RED = '#D32F2F';
const TEAL = '#0A7C70';
const INK = '#1F2733';
const LINE = '#E6E8EC';

const TABS = ['pending', 'approved', 'rejected', 'revoked', 'all'];

function StatPill({ label, value, color }) {
  return (
    <div style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: '12px 18px', minWidth: 120 }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: color || INK }}>{value}</div>
      <div style={{ fontSize: 12, color: '#5B6472', fontWeight: 600 }}>{label}</div>
    </div>
  );
}

function CommunityDiscount() {
  const [tab, setTab] = useState('pending');
  const status = tab === 'all' ? null : tab;
  const { data, loading, refetch } = useQuery(communityDiscountAdminQuery, {
    variables: { status }, fetchPolicy: 'network-only',
  });
  const [review] = useMutation(reviewCommunityDiscountMutation);
  const [saveSettings, { loading: saving }] = useMutation(updateCommunityDiscountSettingsMutation);

  const d = (data && data.communityDiscountAdmin) || {};
  const rows = d.results || [];

  const [form, setForm] = useState(null);
  const f = form || { enabled: !!d.enabled, percent: d.percent || 50, seniorMinAge: d.seniorMinAge || 65, expiryMonths: d.expiryMonths || 12 };

  const onSave = async () => {
    await saveSettings({ variables: { enabled: f.enabled, percent: Number(f.percent), seniorMinAge: Number(f.seniorMinAge), expiryMonths: Number(f.expiryMonths) } });
    setForm(null);
    refetch();
  };

  const act = async (id, action) => {
    let reason = null;
    if (action === 'reject' || action === 'revoke') {
      reason = window.prompt(`Reason for ${action}?`) || '';
    }
    await review({ variables: { id, action, reason } });
    refetch();
  };

  const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginRight: 6 });

  return (
    <div style={{ padding: 24, fontFamily: 'inherit', color: INK }}>
      <h1 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 6px' }}>Senior &amp; Veteran Discount</h1>
      <p style={{ color: '#5B6472', margin: '0 0 20px' }}>
        Community program — approved seniors &amp; veterans pay {f.percent}% off the labour on small jobs.
        The platform waives all its fees (it earns 0); the worker receives the discounted amount net. Small jobs only.
      </p>

      {/* Settings */}
      <div style={{ border: `1px solid ${LINE}`, borderRadius: 14, padding: 20, marginBottom: 22 }}>
        <h2 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 14px' }}>Program settings</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700 }}>
            <input type="checkbox" checked={!!f.enabled} onChange={(e) => setForm({ ...f, enabled: e.target.checked })} />
            Program enabled
          </label>
          <label>Discount %<br /><input type="number" min="0" max="100" value={f.percent} onChange={(e) => setForm({ ...f, percent: e.target.value })} style={{ width: 80, padding: 8, border: `1px solid ${LINE}`, borderRadius: 8 }} /></label>
          <label>Senior min age<br /><input type="number" value={f.seniorMinAge} onChange={(e) => setForm({ ...f, seniorMinAge: e.target.value })} style={{ width: 80, padding: 8, border: `1px solid ${LINE}`, borderRadius: 8 }} /></label>
          <label>Approval valid (months)<br /><input type="number" value={f.expiryMonths} onChange={(e) => setForm({ ...f, expiryMonths: e.target.value })} style={{ width: 80, padding: 8, border: `1px solid ${LINE}`, borderRadius: 8 }} /></label>
          <button onClick={onSave} disabled={saving} style={{ ...btn(TEAL), padding: '10px 20px', fontSize: 14 }}>{saving ? 'Saving…' : 'Save settings'}</button>
        </div>
        <div style={{ marginTop: 10, fontSize: 13, color: f.enabled ? TEAL : RED, fontWeight: 700 }}>
          {f.enabled ? '● Program is LIVE' : '○ Program is OFF (no discounts applied)'}
        </div>
      </div>

      {/* Metrics */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatPill label="Pending applications" value={d.pendingCount || 0} color={RED} />
        <StatPill label="Approved members" value={d.approvedCount || 0} color={TEAL} />
        <StatPill label="Opted-in workers" value={d.optedInWorkers || 0} />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{ background: tab === t ? INK : '#fff', color: tab === t ? '#fff' : INK, border: `1px solid ${LINE}`, borderRadius: 999, padding: '7px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>

      {/* Applications */}
      {loading ? <div>Loading…</div> : (
        <div style={{ border: `1px solid ${LINE}`, borderRadius: 14, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#F6F7F9', textAlign: 'left' }}>
                <th style={{ padding: 12 }}>Applicant</th>
                <th style={{ padding: 12 }}>Type</th>
                <th style={{ padding: 12 }}>Status</th>
                <th style={{ padding: 12 }}>Proof</th>
                <th style={{ padding: 12 }}>Expires</th>
                <th style={{ padding: 12 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan="6" style={{ padding: 20, color: '#5B6472' }}>No applications.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: `1px solid ${LINE}` }}>
                  <td style={{ padding: 12 }}>
                    <div style={{ fontWeight: 700 }}>{r.applicantName || `User #${r.userId}`}</div>
                    <div style={{ fontSize: 12, color: '#5B6472' }}>{r.applicantEmail || ''} {r.applicantPhone || ''}</div>
                  </td>
                  <td style={{ padding: 12, textTransform: 'capitalize', fontWeight: 700, color: r.type === 'veteran' ? TEAL : RED }}>{r.type}</td>
                  <td style={{ padding: 12, textTransform: 'capitalize' }}>{r.status}{r.rejectionReason ? ` — ${r.rejectionReason}` : ''}</td>
                  <td style={{ padding: 12 }}>{r.proofDocument ? <a href={r.proofDocument} target="_blank" rel="noreferrer" style={{ color: TEAL, fontWeight: 700 }}>View</a> : <span style={{ color: '#9AA4AF' }}>—</span>}</td>
                  <td style={{ padding: 12, fontSize: 12 }}>{r.expiresAt ? String(r.expiresAt).slice(0, 10) : '—'}</td>
                  <td style={{ padding: 12, whiteSpace: 'nowrap' }}>
                    {r.status !== 'approved' && <button style={btn(TEAL)} onClick={() => act(r.id, 'approve')}>Approve</button>}
                    {r.status === 'pending' && <button style={btn('#9AA4AF')} onClick={() => act(r.id, 'reject')}>Reject</button>}
                    {r.status === 'approved' && <button style={btn(RED)} onClick={() => act(r.id, 'revoke')}>Revoke</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default CommunityDiscount;
